import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ What the shell's header says, set by the screen that knows.               │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️⚠️ **THE BREADCRUMB USED TO READ THE ROUTE'S DOCUMENT TITLE, AND IT SHOWED.** The header said
 * *"Szervezetek · Control Panel"* — a browser-tab string, suffix and all — and on an organisation it
 * could never say WHICH one, because only the screen holds the tenant it fetched. The mockup's
 * trail is *Szervezetek › Hegyvidék Studio Kft.*, and the second half is data.
 *
 * ⚠️⚠️ **CRUMBS GO THROUGH STATE; ACTIONS GO THROUGH A PORTAL, AND THE SPLIT IS NOT TASTE.** A
 * crumb is text, so a screen can hand it over and the shell can redraw when the text changes. An
 * action is a live control — a button with a busy state and a click handler closing over the
 * screen's own state — and JSX handed through `setState` is a new object on every render, which
 * re-sets the state, which re-renders the screen, which builds new JSX: a loop. Portalled, the
 * button stays the screen's own element and merely appears in the shell's header, where the
 * mockup draws *Invite organisation*.
 */

export interface Crumb {
  readonly label: string;
  /** Absent on the last crumb, which is where you are and therefore not a link. */
  readonly href?: string;
}

interface PageChromeState {
  readonly crumbs: readonly Crumb[];
  readonly setCrumbs: (next: readonly Crumb[]) => void;
  readonly actionsSlot: HTMLElement | null;
  readonly setActionsSlot: (element: HTMLElement | null) => void;
}

const PageChromeContext = createContext<PageChromeState | undefined>(undefined);

export function PageChromeProvider({ children }: { readonly children: ReactNode }): ReactNode {
  const [crumbs, setCrumbs] = useState<readonly Crumb[]>([]);
  const [actionsSlot, setActionsSlot] = useState<HTMLElement | null>(null);
  const value = useMemo(
    () => ({ crumbs, setCrumbs, actionsSlot, setActionsSlot }),
    [crumbs, actionsSlot],
  );
  return <PageChromeContext.Provider value={value}>{children}</PageChromeContext.Provider>;
}

export function usePageChromeState(): PageChromeState | undefined {
  return useContext(PageChromeContext);
}

/**
 * Declare this screen's trail.
 *
 * ⚠️ **Keyed on the crumbs' text, not on the array.** A screen builds its array on every render;
 * keyed on identity, the effect would set state on every render and loop. The newest array is read
 * through a ref, so the effect's only real input is the text it draws.
 *
 * ⚠️ **Cleared on unmount**, or one screen's trail survives into the next.
 */
export function useCrumbs(crumbs: readonly Crumb[]): void {
  const context = useContext(PageChromeContext);
  const setCrumbs = context?.setCrumbs;
  const latest = useRef(crumbs);
  latest.current = crumbs;
  const key = crumbs.map((crumb) => `${crumb.label}\u0000${crumb.href ?? ''}`).join('\u0001');

  useEffect(() => {
    setCrumbs?.(latest.current);
  }, [key, setCrumbs]);

  useEffect(
    () => () => {
      setCrumbs?.([]);
    },
    [setCrumbs],
  );
}

/**
 * The screen's controls, drawn in the shell's header at the right.
 *
 * ⚠️ **Renders nothing until the slot exists**, which is the first commit after the shell mounts.
 * Outside a shell — a route test rendering one screen — there is no slot and nothing is drawn,
 * which is the honest answer to *"where is the header?"* when there is none.
 */
export function HeaderActions({ children }: { readonly children: ReactNode }): ReactNode {
  const slot = useContext(PageChromeContext)?.actionsSlot ?? null;
  return slot === null ? null : createPortal(children, slot);
}
